import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
// The homepage stays eagerly imported: it is the LCP route and the primary
// prerender target, so we never want it behind a lazy chunk.
import Index from "./pages/Index.tsx";
import ScrollToTop from "./components/ScrollToTop.tsx";
import { AdminAuthProvider } from "@/hooks/useAdminAuth";
import { LocaleLayout } from "@/i18n/LocaleProvider";
import { PREFIXED_LANGS, type Lang, parsePath, localizePath, prefixLogicalPath, subscribeBlogSlugs, getBlogSlugVersion } from "@/i18n/locales";
import { loadBlogSlugs } from "@/lib/blogSlugs";
import { useSyncExternalStore } from "react";

loadBlogSlugs();
import GeoRedirect from "@/components/GeoRedirect";
import TrackingProvider from "@/components/TrackingProvider";
import AnalyticsProvider from "@/components/AnalyticsProvider";

// Everything below the homepage is code-split. Route-level lazy loading keeps
// the initial JS bundle small (lower TBT) without hurting SEO: the prerender
// pass waits for real rendered content, so each chunk resolves before the
// snapshot is captured.
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Imprint = lazy(() => import("./pages/Imprint.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const LeadMagnet = lazy(() => import("./pages/LeadMagnet.tsx"));
const CaseStudy = lazy(() => import("./pages/CaseStudy.tsx"));
const BlueprintEngine = lazy(() => import("./pages/BlueprintEngine.tsx"));
const Integrations = lazy(() => import("./pages/Integrations.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const BookingPage = lazy(() => import("./pages/BookingPage.tsx"));

// Admin is a large, authenticated-only surface (rich-text editor, charts,
// realtime). It must never ship in the public bundle.
const AdminGuard = lazy(() => import("@/components/admin/AdminGuard"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin.tsx"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts.tsx"));
const AdminContactDetail = lazy(() => import("./pages/admin/AdminContactDetail.tsx"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews.tsx"));
const AdminHeroLogos = lazy(() => import("./pages/admin/AdminHeroLogos.tsx"));
const AdminFAQ = lazy(() => import("./pages/admin/AdminFAQ.tsx"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog.tsx"));
const BlogPostEditor = lazy(() => import("./pages/admin/BlogPostEditor.tsx"));
const AdminAI = lazy(() => import("./pages/admin/AdminAI.tsx"));
const AdminSEO = lazy(() => import("./pages/admin/AdminSEO.tsx"));
const AdminTracking = lazy(() => import("./pages/admin/AdminTracking.tsx"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics.tsx"));
const AdminBlueprints = lazy(() => import("./pages/admin/AdminBlueprints.tsx"));
const AdminBlueprintDetail = lazy(() => import("./pages/admin/AdminBlueprintDetail.tsx"));
const AdminRedirects = lazy(() => import("./pages/admin/AdminRedirects.tsx"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers.tsx"));
const AdminCustomerDetail = lazy(() => import("./pages/admin/AdminCustomerDetail.tsx"));
const AdminProjectDetail = lazy(() => import("./pages/admin/AdminProjectDetail.tsx"));
const AdminBooking = lazy(() => import("./pages/admin/AdminBooking.tsx"));
const AdminReferences = lazy(() => import("./pages/admin/AdminReferences.tsx"));
const AdminReferenceDetail = lazy(() => import("./pages/admin/AdminReferenceDetail.tsx"));
const AdminAuthors = lazy(() => import("./pages/admin/AdminAuthors.tsx"));

const queryClient = new QueryClient();

/**
 * Localized URLs (e.g. /de/kosten) are mapped onto the logical English route
 * tree (/de/pricing) so every page keeps a single route definition. Old
 * untranslated URLs (/de/pricing) redirect to the localized one.
 */
const LocalizedRoutes = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  useSyncExternalStore(subscribeBlogSlugs, getBlogSlugVersion, getBlogSlugVersion);
  const raw = location.pathname.replace(/\/$/, "") || "/";
  const { lang, path } = parsePath(raw);
  const canonical = localizePath(path, lang);
  if (lang !== "en" && canonical !== raw && !raw.startsWith(`/${lang}/book/`)) {
    return <Navigate to={canonical + location.search + location.hash} replace state={location.state} />;
  }
  const logical = lang === "en" ? raw : prefixLogicalPath(path, lang);
  return <Routes location={{ ...location, pathname: logical }}>{children}</Routes>;
};

// The full public route tree. Mounted once per language base so EN lives at the
// root and DE/EL live under their prefix (Shopify language-URL model).
const publicRoutes = (
  <>
    <Route index element={<Index />} />
    <Route path="imprint" element={<Imprint />} />
    {/* Legacy URL that Google indexed — consolidate onto /imprint */}
    <Route path="impressum" element={<Navigate to="../imprint" replace />} />
    <Route path="privacy-policy" element={<PrivacyPolicy />} />
    <Route path="contact" element={<Contact />} />
    <Route path="blueprint" element={<BlueprintEngine />} />
    <Route path="integrations" element={<Integrations />} />
    <Route path="pricing" element={<Pricing />} />
    <Route path="blog" element={<Blog />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="pages/:slug" element={<LeadMagnet />} />
    <Route path="references/:slug" element={<CaseStudy />} />
    <Route path="*" element={<NotFound />} />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <GeoRedirect />
        <TrackingProvider />
        <AnalyticsProvider />
        <AdminAuthProvider>
          <Suspense fallback={null}>
            <LocalizedRoutes>
              {/* English (default) at the root */}
              <Route element={<LocaleLayout lang="en"><Outlet /></LocaleLayout>}>
                {publicRoutes}
              </Route>

              {/* Prefixed languages */}
              {PREFIXED_LANGS.map((lang) => (
                <Route
                  key={lang}
                  path={`/${lang}`}
                  element={
                    <LocaleLayout lang={lang as Lang}>
                      <Outlet />
                    </LocaleLayout>
                  }
                >
                  {publicRoutes}
                </Route>
              ))}

              {/* Utility (non-localized) */}
              <Route path="/unsubscribe" element={<Unsubscribe />} />

              {/* Booking portal — localized so the language switcher works */}
              <Route
                path="/book/:token"
                element={
                  <LocaleLayout lang="en">
                    <BookingPage />
                  </LocaleLayout>
                }
              />
              {PREFIXED_LANGS.map((lang) => (
                <Route
                  key={`book-${lang}`}
                  path={`/${lang}/book/:token`}
                  element={
                    <LocaleLayout lang={lang as Lang}>
                      <BookingPage />
                    </LocaleLayout>
                  }
                />
              ))}

              {/* Admin (non-localized) */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <AdminGuard>
                    <AdminLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="contacts" element={<AdminContacts />} />
                <Route path="contacts/:id" element={<AdminContactDetail />} />
                <Route path="references" element={<AdminReferences />} />
                <Route path="references/new" element={<AdminReferenceDetail />} />
                <Route path="references/:id" element={<AdminReferenceDetail />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="hero-logos" element={<AdminHeroLogos />} />
                <Route path="faq" element={<AdminFAQ />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="blog/new" element={<BlogPostEditor />} />
                <Route path="blog/:id/edit" element={<BlogPostEditor />} />
                <Route path="ai" element={<AdminAI />} />
                <Route path="seo" element={<AdminSEO />} />
                <Route path="tracking" element={<AdminTracking />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="blueprints" element={<AdminBlueprints />} />
                <Route path="blueprints/:id" element={<AdminBlueprintDetail />} />
                <Route path="redirects" element={<AdminRedirects />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="customers/:id" element={<AdminCustomerDetail />} />
                <Route path="customers/:customerId/projects/:projectId" element={<AdminProjectDetail />} />
                <Route path="booking" element={<AdminBooking />} />
                <Route path="authors" element={<AdminAuthors />} />
              </Route>
            </LocalizedRoutes>
          </Suspense>
        </AdminAuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
